import clsx from 'clsx'
import { HugeiconsIcon } from '@hugeicons/react'
import { useLingui } from '@lingui/react/macro'

import {
  createContext,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  DragDropVerticalIcon
} from '@hugeicons/core-solid-rounded'

import {
  draggable,
  dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { GoodieFile, isNewFile, DropPositions } from '../types'

interface DragAndDropContextState {
  dragHandleRef?: RefObject<HTMLDivElement | null>
  onLeft: () => void
  onRight: () => void
}

const Context = createContext<DragAndDropContextState>({
  onLeft: () => {},
  onRight: () => {}
})

type RootProps = React.PropsWithChildren<{
  file: GoodieFile
  currentPosition: number
  maxPosition: number
  onChangePosition: (newPosition: number) => void
}>

export function Root({
  children,
  file,
  currentPosition,
  maxPosition,
  onChangePosition
}: RootProps) {
  const elementRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)

  const [, setDragging] = useState(false)
  const [draggedOver, setDraggedOver] = useState(false)
  const [overPosition, setOverPosition] = useState<DropPositions | null>(null)

  useEffect(() => {
    if (!elementRef.current) return
    if (!handleRef.current) return

    const name = isNewFile(file) ? file.file.name : file.name

    return draggable({
      element: elementRef.current,
      dragHandle: handleRef.current,
      getInitialDataForExternal: () => ({ 'text/plain': name }),
      onDragStart: () => setDragging(true),
      onDrop({ location }) {
        setDragging(false)

        const destination = location.current.dropTargets[0]

        if (!destination) return

        const position = destination.data.position as number
        const overPosition = destination.data.overPosition as DropPositions

        if (overPosition === DropPositions.left) {
          if (position > currentPosition) onChangePosition(position - 1)
          else onChangePosition(position)
        }

        if (overPosition === DropPositions.right) {
          if (position > currentPosition) onChangePosition(position)
          else onChangePosition(position + 1)
        }
      }
    })
  }, [file, currentPosition, setDragging, onChangePosition])

  useEffect(() => {
    if (!elementRef.current) return

    return dropTargetForElements({
      element: elementRef.current,
      onDragEnter: () => setDraggedOver(true),
      onDragLeave: () => setDraggedOver(false),
      onDrag({ location }) {
        const mouseX = location.current.input.clientX
        const elementRect = elementRef.current?.getBoundingClientRect()

        if (!elementRect) return

        const elementMiddle = elementRect.left + elementRect.width / 2

        setOverPosition(
          mouseX < elementMiddle ? DropPositions.left : DropPositions.right
        )
      },
      onDrop: () => setDraggedOver(false),
      getData: () => ({ position: currentPosition, overPosition })
    })
  }, [currentPosition, setDraggedOver, overPosition])

  const onLeft = useCallback(() => {
    onChangePosition(Math.max(0, currentPosition - 1))
  }, [currentPosition, onChangePosition])

  const onRight = useCallback(() => {
    onChangePosition(Math.min(maxPosition, currentPosition + 1))
  }, [currentPosition, maxPosition, onChangePosition])

  const value = useMemo(() => {
    return {
      dragHandleRef: handleRef,
      onLeft,
      onRight
    }
  }, [handleRef, onRight, onLeft])

  return (
    <article
      ref={elementRef}
      // The translation 0 is needed since there is a chrome bug that f`s up the drag preview without it
      // see: https://github.com/react-dnd/react-dnd/issues/832#issuecomment-442071628
      className="relative flex flex-row items-stretch gap-1.5 translate-x-0 translate-y-0"
    >
      <div
        className={clsx(
          '-ml-px w-[2px] rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.left && 'opacity-100'
        )}
      />
      <Context value={value}>{children}</Context>
      <div
        className={clsx(
          '-mr-px w-[2px] rounded bg-primary opacity-0',
          draggedOver && overPosition === DropPositions.right && 'opacity-100'
        )}
      />
    </article>
  )
}

export function GalleryMoveButtons() {
  const { t } = useLingui()
  const { dragHandleRef, onLeft, onRight } = useContext(Context)

  return (
    <div className="flex flex-row items-center gap-1">
      <div
        className="size-5 cursor-grab rounded-[5px] text-text hover:bg-primary-bright"
        ref={dragHandleRef}
      >
        <HugeiconsIcon icon={DragDropVerticalIcon} className="size-full" />
      </div>
      <button
        className="size-5 text-text"
        type="button"
        aria-label={t({
          comment:
            'Button label to describe to the user that they can move a file to the left in a list',
          message: `Move left`
        })}
        tabIndex={0}
        onClick={onLeft}
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} className="size-full" />
      </button>
      <button
        className="size-5 text-text"
        type="button"
        aria-label={t({
          comment:
            'Button label to describe to the user that they can move a file to the right in a list',
          message: `Move right`
        })}
        tabIndex={0}
        onClick={onRight}
      >
        <HugeiconsIcon icon={ArrowRight01Icon} className="size-full" />
      </button>
    </div>
  )
}
